"use client";

import Image from "next/image";
import { useEffect, useId, useRef, useState } from "react";
import { Minus, Package, Plus, ShoppingCart, Trash2, X } from "lucide-react";
import { formatCurrency } from "@/lib/order";
import { completedRetailCheckout, isRetailCartEdited, markRetailCartEdited } from "@/lib/retail";
import type { CartItem } from "@/types/cart";
import type { PublicCompany } from "@/types/store";
import { retailTheme } from "./retail-theme";
import styles from "./cart-drawer.module.css";

type Props = {
  company: PublicCompany;
  items: CartItem[];
  total: number;
  onClose: () => void;
  onCheckout: () => void;
  onUpdateQuantity: (item: CartItem, quantity: number) => void;
  onRemove: (item: CartItem) => void;
};

// Same stock and price rules as the storefront; checkout revalidates on the server.
function quantityLimit(item: CartItem) {
  const source = item.variation ?? item.product;
  return source.track_stock ? Math.min(100, source.stock) : 100;
}

function unitPrice(item: CartItem) {
  return Number(item.quoted_unit_price ?? (Number(item.variation?.current_price ?? item.product.current_price)
    + (item.selected_options ?? []).reduce((sum, option) => sum + Number(option.price_delta), 0)));
}

function ProductImage({ item }: { item: CartItem }) {
  const [missing, setMissing] = useState<string | null>(null);
  const image = item.variation?.image_url || item.product.primary_image_url || item.product.images?.[0]?.image_url;
  return image && image !== missing
    ? <Image unoptimized width={80} height={88} src={image} alt={item.product.name} className={styles.image} onError={() => setMissing(image)}/>
    : <span className={styles.image} aria-hidden="true"><Package size={28}/></span>;
}

export default function RetailCartDrawer({ company, items, total, onClose, onCheckout, onUpdateQuantity, onRemove }: Props) {
  const dialog = useRef<HTMLDialogElement>(null);
  const title = useId();
  const savedPurchase = Boolean(completedRetailCheckout(company.slug) && !isRetailCartEdited(company.slug));
  useEffect(() => {
    const element = dialog.current;
    const focus = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    element?.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      element?.close();
      document.body.style.overflow = overflow;
      focus?.focus({ preventScroll: true });
    };
  }, []);

  function changeQuantity(item: CartItem, quantity: number) {
    const next = Math.max(1, Math.min(quantity, quantityLimit(item)));
    if (next === item.quantity) return;
    markRetailCartEdited(company.slug);
    onUpdateQuantity(item, next);
  }

  return <dialog ref={dialog} aria-labelledby={title} className={styles.drawer}
    style={retailTheme(company.primary_color ?? "")}
    onCancel={event => { event.preventDefault(); onClose(); }}
    onKeyDown={event => {
      if (event.key !== "Tab") return;
      const buttons = Array.from(dialog.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []);
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialog.current?.contains(document.activeElement))) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus();
      }
    }}
    onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <header className={styles.header}>
      <div><h2 id={title} className={styles.title}><ShoppingCart size={23}/>Meu carrinho</h2>
        <p className={styles.caption}>{company.name} · {items.reduce((sum, item) => sum + item.quantity, 0)} item(ns)</p></div>
      <button type="button" aria-label="Fechar carrinho" onClick={onClose} className={styles.iconButton}><X size={22}/></button>
    </header>
    <div className={styles.items}>
      {!items.length && <div className={styles.empty}><ShoppingCart size={36}/><p>Seu carrinho está vazio</p>
        {savedPurchase && <p className={styles.caption}>Você tem uma compra salva. Acesse a finalização para conferir o pedido e continuar.</p>}</div>}
      {items.map((item, index) => {
        return <article key={index} className={styles.item}>
          <ProductImage item={item}/>
          <div className={styles.itemBody}>
            <h3 className={styles.productName}>{item.product.name}</h3>
            {item.variation && <p className={styles.caption}>{item.variation.display_name || item.variation.sku}</p>}
            {(item.selected_options ?? []).map(option => <p key={option.option_id} className={styles.caption}>{option.group_name}: {option.option_name}</p>)}
            {item.observation && <p className={styles.caption}>{item.observation}</p>}
            <p className={styles.caption}>{formatCurrency(unitPrice(item))} cada</p>
            <div className={styles.itemActions}><div className={styles.quantity}>
              <button type="button" aria-label={`Diminuir ${item.product.name}`} className={styles.iconButton} disabled={item.quantity <= 1} onClick={() => changeQuantity(item, item.quantity - 1)}><Minus size={16}/></button>
              <span aria-label={`Quantidade de ${item.product.name}`}>{item.quantity}</span>
              <button type="button" aria-label={`Aumentar ${item.product.name}`} className={styles.iconButton} disabled={item.quantity >= quantityLimit(item)} onClick={() => changeQuantity(item, item.quantity + 1)}><Plus size={16}/></button>
            </div><strong>{formatCurrency(unitPrice(item) * item.quantity)}</strong></div>
          </div>
          <button type="button" aria-label={`Remover ${item.product.name}`} className={styles.iconButton} onClick={() => { markRetailCartEdited(company.slug); onRemove(item); }}><Trash2 size={18}/></button>
        </article>;
      })}
    </div>
    <footer className={styles.footer}>
      <div className={styles.subtotal}><span>Subtotal</span><strong>{formatCurrency(total)}</strong></div>
      <p className={styles.caption}>Frete e descontos calculados na finalização.</p>
      <button type="button" className={styles.primary} disabled={!items.length && !savedPurchase} onClick={onCheckout}>Finalizar compra</button>
      <button type="button" className={styles.secondary} onClick={onClose}>Continuar comprando</button>
    </footer>
  </dialog>;
}
